import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Edit2, Check, X, Plus, BookOpen, Users, Sparkles, ArrowRight, ImageIcon, Layers, CalendarDays, Camera } from "lucide-react"
import { useEffect, useState, type CSSProperties } from "react"
import { cn } from "@/lib/utils"
import ShareAlbumDialog from "@/components/ShareAlbumDialog"
import FittedCoverPreview from '@/components/shared/yearbook-preview/FittedCoverPreview';

interface AlbumCreation {
  id: number
  image: string
  format: string
  pages: string
}

interface AlbumProject {
  id: number
  name: string
  created_date: string
  members: number
  ordered: boolean
  photos?: number
  coverPage?: unknown
  aspectRatio?: number
  pageWidth?: number
  pageHeight?: number
  creation: AlbumCreation
}

function csrfToken() {
  return typeof document !== "undefined"
    ? document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    : ""
}

// Données de repli utilisées quand /social_networks.json n'est pas disponible (dev sans backend).
const defaultProjects: AlbumProject[] = [
  {
    id: 1,
    name: "Grèce 2024",
    created_date: "19/12/2024",
    members: 4,
    ordered: false,
    photos: 128,
    creation: {
      id: 1,
      image: "/images/products/carre_21.png",
      format: "Carré (21×21cm)",
      pages: "34 pages",
    },
  },
  {
    id: 2,
    name: "Naissance de Léa",
    created_date: "02/11/2024",
    members: 6,
    ordered: true,
    photos: 213,
    creation: {
      id: 2,
      image: "/images/products/carre_21.png",
      format: "Carré (21×21cm)",
      pages: "48 pages",
    },
  },
]

// JSON attendu depuis GET /social_networks.json :
// { "projects": [ { id, name, created_date, members, ordered, photos, creation: { id, image, format, pages } } ] }
// ou directement [ ... ]

export default function SocialNetworks() {
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false)
  const [joinCode, setJoinCode] = useState("")
  const [projects, setProjects] = useState<AlbumProject[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch("/social_networks.json", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data: { projects?: AlbumProject[] } | AlbumProject[]) => {
        const list = Array.isArray(data) ? data : data.projects ?? []
        setProjects(list)
        if (list.length === 0) setShowWelcomeDialog(true)
      })
      .catch(() => {
        // Endpoint absent en dev : on affiche les données de démonstration.
        setProjects(defaultProjects)
      })
      .finally(() => setLoading(false))
  }, [])

  const handleRenamed = (id: number, newName: string) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name: newName } : p)))
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-accent/5">
      <Navigation />

      {/* Welcome Dialog — s'ouvre uniquement si la liste est vide */}
      <Dialog open={showWelcomeDialog} onOpenChange={setShowWelcomeDialog}>
        <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden rounded-3xl">
          <div className="grid md:grid-cols-[1.5fr,1fr]">
            {/* Créer un album */}
            <div className="relative bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 p-10 flex flex-col items-center justify-center">
              <div className="absolute top-6 right-6">
                <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Recommandé
                </Badge>
              </div>

              <DialogHeader className="text-center mb-8">
                <DialogTitle className="text-3xl font-display font-bold mb-2">
                  Créer un album
                </DialogTitle>
                <DialogDescription className="text-base">
                  Commencez votre projet et invitez vos proches
                </DialogDescription>
              </DialogHeader>

              <div className="mb-8 transform hover:scale-105 transition-transform">
                <img
                  src="/images/products/carre_fuji_20-2.png"
                  alt="Album example"
                  className="w-56 h-56 object-cover rounded-2xl shadow-2xl ring-4 ring-primary/20"
                />
              </div>

              <Button
                variant="rainbow"
                size="lg"
                className="w-full max-w-xs text-lg h-14"
                asChild
              >
                <a href="/#albums" onClick={() => setShowWelcomeDialog(false)}>
                  Créer mon album
                </a>
              </Button>
            </div>

            {/* Rejoindre un groupe */}
            <div className="bg-background p-10 flex flex-col justify-center border-l">
              <DialogHeader className="text-center mb-6">
                <DialogTitle className="text-xl font-display">
                  Rejoindre un groupe
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Pour rejoindre un groupe, rentre ton code :
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <Input
                  placeholder="Code d'invitation"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  className="text-center text-lg h-12 border-2"
                />

                <form
                  action="/social_networks/send_join"
                  method="POST"
                  onSubmit={(e) => {
                    // Insère le jeton CSRF et le code, puis laisse le navigateur soumettre
                    // et suivre la redirection HTML renvoyée par le serveur.
                    const form = e.currentTarget
                    const csrf = document.createElement("input")
                    csrf.type = "hidden"
                    csrf.name = "authenticity_token"
                    csrf.value = csrfToken()
                    form.appendChild(csrf)
                    // Laisse le navigateur faire la navigation native (→ redirection HTML)
                  }}
                >
                  <input type="hidden" name="code" value={joinCode} />
                  <Button
                    type="submit"
                    variant="outline"
                    size="lg"
                    className="w-full"
                    disabled={!joinCode.trim()}
                  >
                    Rejoindre un album invité
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <main className="container mx-auto px-4 lg:px-8 pt-[120px] pb-12">
        {/* Header */}
        <div className="mb-14 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-4 mb-4">
            <span className="h-0.5 w-10 bg-gradient-to-r from-transparent to-primary/60"></span>
            <h1 className="text-4xl md:text-5xl font-display font-bold leading-none bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] bg-clip-text text-transparent">
              Mes créations
            </h1>
            <span className="h-0.5 w-10 bg-gradient-to-l from-transparent to-primary/60"></span>
          </div>
          <p className="text-muted-foreground text-base md:text-lg mb-6 whitespace-nowrap overflow-hidden text-ellipsis">
            Vos albums photo, prêts à être édités. Reprenez là où vous vous êtes arrêté et donnez vie à vos souvenirs.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="rainbow" size="lg" asChild>
              <a href="/#albums">Choisir un album</a>
            </Button>
            <Button variant="rainbow" size="lg" onClick={() => setShowWelcomeDialog(true)}>
              Rejoindre un album
            </Button>
          </div>
        </div>

        {/* Albums */}
        {loading && (
          <div className="text-center text-muted-foreground py-16">Chargement de vos albums…</div>
        )}

        {!loading && projects.length === 0 && (
          <Card className="max-w-2xl mx-auto border-border/50 bg-card/80 text-center">
            <div className="py-16 px-6 flex flex-col items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <ImageIcon className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-xl font-display font-semibold text-foreground">
                Aucun album pour le moment
              </h3>
              <p className="text-muted-foreground max-w-sm">
                Choisissez un album pour commencer votre projet, ou rejoignez un album
                partagé avec un code d'invitation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <Button variant="rainbow" asChild>
                  <a href="/#albums">Choisir un album</a>
                </Button>
                <Button variant="outline" onClick={() => setShowWelcomeDialog(true)}>
                  Rejoindre un album
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {projects.map((project) => (
            <AlbumCard key={project.id} project={project} onRenamed={handleRenamed} />
          ))}
        </div>
      </main>

      <Footer />
    </div>
  )
}

function AlbumCard({
  project,
  onRenamed,
}: {
  project: AlbumProject
  onRenamed: (id: number, newName: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState(project.name)
  const [saving, setSaving] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null)

  // Calcule la taille d'affichage pour que le cadre épouse le ratio de l'image
  // sans dépasser 208px (w-52) dans la plus grande dimension.
  const MAX_FRAME = 208
  const frameStyle: CSSProperties = (() => {
    if (!imgDims) return { width: "13rem", height: "13rem" }
    const ratio = imgDims.w / imgDims.h
    if (ratio >= 1) return { width: `${MAX_FRAME}px`, height: `${Math.round(MAX_FRAME / ratio)}px` }
    return { height: `${MAX_FRAME}px`, width: `${Math.round(MAX_FRAME * ratio)}px` }
  })()

  const startEdit = () => {
    setDraftName(project.name)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setDraftName(project.name)
  }

  const saveName = () => {
    const trimmed = draftName.trim()
    if (!trimmed || trimmed === project.name) {
      setEditing(false)
      return
    }
    setSaving(true)
    const body = new URLSearchParams()
    body.append("_method", "patch")
    body.append("social_network[name]", trimmed)
    fetch(`/social_networks/${project.id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: body.toString(),
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status))
        onRenamed(project.id, trimmed)
        setEditing(false)
      })
      .catch(() => undefined)
      .finally(() => setSaving(false))
  }

  return (
    <Card className="group overflow-hidden rounded-2xl border border-border/60 bg-card shadow-card hover:shadow-elegant transition-all duration-500">
      <div className="flex flex-col md:flex-row">
        {/* Produit — l'album mis en avant, grand */}
          <div className="relative md:w-[42%] shrink-0 bg-gradient-to-br from-primary/8 via-accent/10 to-secondary/8 flex items-center justify-center px-6 pt-14 pb-6 md:px-8 md:pt-14 md:pb-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,hsl(var(--primary)/0.10),transparent_60%)]" />
          {/* Badge format flottant */}
          <div className="absolute top-4 left-4 z-10">
            <Badge variant="secondary" className="bg-background/80 backdrop-blur border-border/40 text-foreground">
              <Layers className="w-3 h-3 mr-1" />
              {project.creation.format}
            </Badge>
          </div>
          <a
            href={`/social_networks/${project.id}/editor`}
            className="relative block transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-1deg]"
          >
            {project.coverPage ? (
              <FittedCoverPreview
                className="rounded-xl overflow-hidden border border-border/40 shadow-2xl ring-1 ring-primary/10"
                style={(() => {
                  const r = Number(project.aspectRatio) > 0 ? Number(project.aspectRatio) : 1
                  return r >= 1
                    ? { width: 208, height: Math.round(208 / r) }
                    : { height: 240, width: Math.round(240 * r) }
                })()}
                page={project.coverPage}
                assetsBaseUrl="https://www.wememberz.com"
                aspectRatio={project.aspectRatio}
                pageWidth={project.pageWidth}
                pageHeight={project.pageHeight}
              />
            ) : (
              <div
                className="rounded-xl overflow-hidden border border-border/40 shadow-2xl ring-1 ring-primary/10 transition-[width,height] duration-300"
                style={frameStyle}
              >
                <img
                  src={project.creation.image}
                  alt={project.creation.format}
                  className="w-full h-full object-cover"
                  onLoad={(e) => {
                    const img = e.currentTarget
                    if (img.naturalWidth && img.naturalHeight) {
                      setImgDims({ w: img.naturalWidth, h: img.naturalHeight })
                    }
                  }}
                />
              </div>
            )}
            {/* « dos » du livre pour l'effet relief */}
            <div className="absolute -right-1 top-1 bottom-1 w-2 rounded-r bg-gradient-to-b from-primary/30 to-secondary/30 shadow-md" />
          </a>
        </div>

        {/* Contenu — nom, infos, édition */}
        <div className="flex-1 p-6 md:p-7 flex flex-col">
          {/* Titre + édition du nom */}
          <div className="mb-1">
            {editing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveName()
                    if (e.key === "Escape") cancelEdit()
                  }}
                  className="text-2xl font-display font-bold h-10"
                  autoFocus
                  disabled={saving}
                />
                <Button
                  size="icon"
                  variant="rainbow"
                  className="h-10 w-10 shrink-0"
                  onClick={saveName}
                  disabled={saving}
                  title="Enregistrer"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10 shrink-0"
                  onClick={cancelEdit}
                  disabled={saving}
                  title="Annuler"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <a
                  href={`/social_networks/${project.id}/editor`}
                  className="font-display font-bold text-2xl text-foreground leading-tight hover:text-primary transition-colors"
                >
                  {project.name}
                </a>
                <button
                  onClick={startEdit}
                  className="text-muted-foreground/60 hover:text-primary transition-colors mt-1 shrink-0"
                  title="Renommer l'album"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1.5 mb-4">
            <CalendarDays className="w-3.5 h-3.5" />
            Créé le {project.created_date}
          </p>

          {/* Infos produit */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 px-3 py-1 text-xs font-medium text-foreground">
              <BookOpen className="w-3.5 h-3.5 text-primary" />
              {project.creation.pages}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 px-3 py-1 text-xs font-medium text-foreground">
              <Users className="w-3.5 h-3.5 text-primary" />
              {project.members} membre{project.members > 1 ? "s" : ""}
            </span>
            {typeof project.photos === "number" && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 px-3 py-1 text-xs font-medium text-foreground">
                <Camera className="w-3.5 h-3.5 text-primary" />
                {project.photos} photo{project.photos > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* CTA — édition du livre, action principale */}
          <div className="mt-auto">
            <Button variant="rainbow" size="lg" className="w-full h-12" asChild>
              <a href={`/social_networks/${project.id}/editor`}>
                <BookOpen className="w-5 h-5 mr-2" />
                Éditer l'album
              </a>
            </Button>

            {/* Actions secondaires — discrètes */}
            <div className="flex items-center justify-center gap-4 mt-4 text-sm">
              <button
                onClick={() => setShareOpen(true)}
                className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5"
              >
                <Users className="w-4 h-4" />
                Membres & invitations
              </button>
              <ShareAlbumDialog
                projectId={project.id}
                projectName={project.name}
                open={shareOpen}
                onOpenChange={setShareOpen}
              />
              <span className="text-border/70">|</span>
              <a
                href={`/social_networks/${project.id}/albums`}
                className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5"
              >
                Voir les photos
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}